import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Card, Modal } from 'react-bootstrap';
import Sidebar from '../../../../Components/Sidebar';
import getEnvConfig from '../.././../../config';
import { fetchToken, clearToken } from '../../../../Auth';

function ChargerRegistration() {
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [isSuperUser, setIsSuperUser] = useState(false);
  const [locations, setLocations] = useState([]);
  const [groups, setGroups] = useState([]);
  const [connectorTypes, setConnectorTypes] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [creatingNewLocation, setCreatingNewLocation] = useState(false);
  const [creatingNewGroup, setCreatingNewGroup] = useState(false);
  const [usernameSubmitted, setUsernameSubmitted] = useState(false);
  const [numPorts, setNumPorts] = useState(1);

  // New state for location creation feedback
  const [locationCreationResult, setLocationCreationResult] = useState(null);
  const [locationCreationError, setLocationCreationError] = useState("");
  // State for editing lat/lng
  const [isEditingLatLng, setIsEditingLatLng] = useState(false);
  const [editLat, setEditLat] = useState("");
  const [editLng, setEditLng] = useState("");
  const [editLatLngError, setEditLatLngError] = useState("");
  const [editLatLngSuccess, setEditLatLngSuccess] = useState("");

  const [isModalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState('');
  const { backendUrl } = getEnvConfig();
  const token = fetchToken();

  const [formData, setFormData] = useState({
    token: token,
    sim_iccid: "",
    serial_number: "",
    charger_model_name: "",
    price_per_kwh: "",
    charging_rate: "",
    charger_amps: "",
    location_id: "",
    group_id: "",
    address: "",
    unit: "",
    city: "",
    state: "",
    country: "US",  // Default country
    zipcode: "",
    group_name: "",
    total_max_amp: "",
    utility_id: "",
    username: "",
    num_ports: 1,
    ports: [
      {
        port_number: 1,
        connector_type_id: 1,
        max_power_kw: "",
        port_amps: "",
        charging_rate: "",
        ocpp_connector_id: 1
      }
    ],
    negotiated_rate: {
      fixed_markup: 0,
      owner_percentage: 80,
      ev_owner_percentage: 20,
      start_date: new Date().toISOString().split('T')[0]
    }
  });



  // Fetch connector types
  useEffect(() => {
    const fetchConnectorTypes = async () => {
      try {
        const response = await fetch(`${backendUrl}/chargers/connector-types`);
        const data = await response.json();
        if (data.status === "success") {
          setConnectorTypes(data.connector_types || []);
        }
      } catch (error) {
        console.error("Error fetching connector types:", error);
      }
    };
    
    fetchConnectorTypes();
  }, [backendUrl]);

  const fetchLocations = async (username) => {
    const response = await fetch(`${backendUrl}/chargers/get_locations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_token: token, username }),
    });
    const data = await response.json();
    setLocations(data.locations || []);
  };

  const handleUsernameSubmit = (e) => {
    e.preventDefault();
    setUsernameSubmitted(true);
    fetchLocations(formData.username);
  };

  useEffect(() => {
    const checkSuperUser = async () => {
      try {
        const response = await fetch(`${backendUrl}/users/check_superuser`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_token: token }),
        });
        const data = await response.json();
        if (data.super_user) {
          setIsSuperUser(true);
        } else {
          alert('Access denied. You are not a superuser.');
          navigate('/userdashboard');
        }
      } catch (error) {
        clearToken();
        navigate('/login');
      }
    };

    if (token) {
      checkSuperUser();
    } else {
      navigate('/login');
    }
  }, [backendUrl, navigate, token]);

  const fetchGroups = async (location_id) => {
    const response = await fetch(`${backendUrl}/chargers/get_groups`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ location_id, user_token: token, username: formData.username }),
    });
    const data = await response.json();
    setGroups(data.groups || []);
  };

  const handleLocationChange = (e) => {
    const location_id = e.target.value;
    setSelectedLocation(location_id);
    fetchGroups(location_id);
    setFormData({ ...formData, location_id });
    setCreatingNewGroup(false);
  };

  const handleGroupChange = (e) => {
    const group_id = e.target.value;
    setSelectedGroup(group_id);
    setFormData({ ...formData, group_id });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handlePortChange = (index, field, value) => {
    const updatedPorts = [...formData.ports];
    updatedPorts[index] = {
      ...updatedPorts[index],
      [field]: field === 'port_number' || field === 'connector_type_id' || field === 'ocpp_connector_id' 
        ? parseInt(value, 10) 
        : value
    };
    
    setFormData({
      ...formData,
      ports: updatedPorts,
    });
  };

  const handleRateChange = (field, value) => {
    setFormData({
      ...formData,
      negotiated_rate: {
        ...formData.negotiated_rate,
        [field]: field === 'start_date' || field === 'end_date' ? value : parseFloat(value),
      }
    });
  };

  const addPort = () => {
    if (formData.ports.length < 8) { // Limit to 8 ports max
      const newPort = {
        port_number: formData.ports.length + 1,
        connector_type_id: 1, // Default connector type
        max_power_kw: "",
        port_amps: "",
        charging_rate: "",
        ocpp_connector_id: formData.ports.length + 1
      };
      
      setFormData({
        ...formData,
        num_ports: formData.ports.length + 1,
        ports: [...formData.ports, newPort]
      });
      
      setNumPorts(numPorts + 1);
    }
  };

  const removePort = (index) => {
    if (formData.ports.length > 1) {
      const updatedPorts = [...formData.ports];
      updatedPorts.splice(index, 1);
      
      // Renumber port_number and ocpp_connector_id
      const renumberedPorts = updatedPorts.map((port, idx) => ({
        ...port,
        port_number: idx + 1,
        ocpp_connector_id: idx + 1
      }));
      
      setFormData({
        ...formData,
        num_ports: renumberedPorts.length,
        ports: renumberedPorts
      });
      
      setNumPorts(numPorts - 1);
    }
  };

  const createNewLocation = async () => {
    setLocationCreationError("");
    setLocationCreationResult(null);
    try {
      const response = await fetch(`${backendUrl}/chargers/create_location`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_token: token,
          address: formData.address,
          unit: formData.unit,
          city: formData.city,
          state: formData.state,
          country: formData.country,
          zipcode: formData.zipcode,
          username: formData.username,
        }),
      });
      const data = await response.json();
      if (data.status === 'success') {
        setSelectedLocation(data.location_id);
        setCreatingNewLocation(false);
        setLocationCreationResult(data.location_obj);
        setLocationCreationError("");
        fetchLocations(formData.username);
      } else {
        setLocationCreationError(data.message || 'Failed to create location');
        setLocationCreationResult(null);
      }
    } catch (error) {
      setLocationCreationError(error.message || 'Failed to create location');
      setLocationCreationResult(null);
    }
  };

  const createNewGroup = async () => {
    try {
      console.log({
        user_token: token,
        location_id: selectedLocation,
        group_name: formData.group_name,
        total_max_amp: formData.total_max_amp,
        utility_id: formData.utility_id,
        username: formData.username,
      });
      const response = await fetch(`${backendUrl}/chargers/create_group`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_token: token,
          location_id: selectedLocation,
          group_name: formData.group_name,
          total_max_amp: formData.total_max_amp,
          utility_id: formData.utility_id,
          username: formData.username,
        }),
      });
      const data = await response.json();
      if (data.status === 'success') {
        setSelectedGroup(data.group_id);
        setCreatingNewGroup(false);
        fetchGroups(selectedLocation);
      } else {
        alert('Failed to create group');
      }
    } catch (error) {
      alert(error);
    }
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${backendUrl}/chargers/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const reply = await response.json();

      if (response.ok && reply.res === 'created') {
        openModal('Registration successful!');
        navigate('/users/charger_registration_display', {
          state: {
            imageData: `data:image/png;base64,${reply.img}`,
            uuid: reply.uuid,
            ports: reply.ports ? reply.ports.map(port => ({
              ...port,
              qr_code: `data:image/png;base64,${port.qr_code}`
            })) : []
          },
        });
      } else {
        openModal('Error: ' + JSON.stringify(reply.res));
      }
    } catch (error) {
      alert(error);
    }
  };

  const openModal = (content) => {
    setModalContent(content);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalContent('');
  };

  // When toggling create location, clear feedback
  const handleToggleCreateLocation = () => {
    setCreatingNewLocation(!creatingNewLocation);
    setLocationCreationResult(null);
    setLocationCreationError("");
    setIsEditingLatLng(false);
    setEditLatLngError("");
    setEditLatLngSuccess("");
  };

  // When locationCreationResult changes, set editLat/editLng to current values
  useEffect(() => {
    if (locationCreationResult) {
      setEditLat(locationCreationResult.latitude);
      setEditLng(locationCreationResult.longitude);
    }
  }, [locationCreationResult]);

  const handleEditLatLngSubmit = async () => {
    setEditLatLngError("");
    setEditLatLngSuccess("");
    try {
      const response = await fetch(`${backendUrl}/chargers/update_location_latlng`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_token: token,
          location_id: locationCreationResult.id,
          latitude: parseFloat(editLat),
          longitude: parseFloat(editLng),
        }),
      });
      const data = await response.json();
      if (data.status === 'success') {
        setEditLatLngSuccess('Latitude/Longitude updated successfully.');
        setEditLatLngError("");
        setLocationCreationResult({ ...locationCreationResult, latitude: parseFloat(editLat), longitude: parseFloat(editLng) });
        setIsEditingLatLng(false);
      } else {
        setEditLatLngError(data.message || 'Failed to update latitude/longitude.');
        setEditLatLngSuccess("");
      }
    } catch (error) {
      setEditLatLngError(error.message || 'Failed to update latitude/longitude.');
      setEditLatLngSuccess("");
    }
  };

  const handleEditLatLngCancel = () => {
    setIsEditingLatLng(false);
    setEditLat(locationCreationResult.latitude);
    setEditLng(locationCreationResult.longitude);
    setEditLatLngError("");
    setEditLatLngSuccess("");
  };

  if (!isSuperUser) return null;

  return (
    <div className="d-flex">
      <Sidebar />
      <Container fluid className="py-4 px-lg-5">
        <Row>
          <Col lg={8} className="mx-auto">
            <Card>
              <Card.Header>
                <h4 className="mb-0">Charger Registration</h4>
              </Card.Header>
              <Card.Body>
                {!usernameSubmitted ? (
                  <Form onSubmit={handleUsernameSubmit}>
                    <Form.Group controlId="username" className="mb-3">
                      <Form.Label>Charger Owner Username</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter username"
                        name="username"
                        onChange={handleChange}
                        value={formData.username}
                        required
                      />
                    </Form.Group>
                    <Button variant="primary" type="submit" className="w-100">
                      Continue
                    </Button>
                  </Form>
                ) : (
                  <Form onSubmit={submitHandler}>
                    {/* Location creation feedback */}
                    {locationCreationError && (
                      <div className="alert alert-danger" role="alert">
                        {locationCreationError}
                      </div>
                    )}
                    {locationCreationResult && (
                      <div className="alert alert-success" role="alert">
                        <div><strong>Result: {locationCreationResult.description}</strong></div>
                        <div>
                          <strong>Address:</strong> {locationCreationResult.address}, {locationCreationResult.city}, {locationCreationResult.state}, {locationCreationResult.country} {locationCreationResult.zipcode}
                        </div>
                        <div>
                          <strong>Latitude:</strong> {locationCreationResult.latitude}, <strong>Longitude:</strong> {locationCreationResult.longitude}
                        </div>
                        <div>
                          <strong>Location ID:</strong> {locationCreationResult.id}
                        </div>
                        <div>
                          <a
                            href={`https://www.google.com/maps/place/${locationCreationResult.latitude},${locationCreationResult.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View on Google Maps
                          </a>
                        </div>
                        <div className="mt-3">
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => setIsEditingLatLng((v) => !v)}
                          >
                            {isEditingLatLng ? 'Cancel Edit Latitude/Longitude' : 'Edit Latitude/Longitude'}
                          </button>
                        </div>
                        {isEditingLatLng && (
                          <div className="mt-3">
                            <div className="mb-2">
                              <label htmlFor="edit-lat" className="form-label">Latitude</label>
                              <input
                                id="edit-lat"
                                type="number"
                                step="any"
                                className="form-control"
                                value={editLat}
                                onChange={e => setEditLat(e.target.value)}
                                required
                              />
                            </div>
                            <div className="mb-2">
                              <label htmlFor="edit-lng" className="form-label">Longitude</label>
                              <input
                                id="edit-lng"
                                type="number"
                                step="any"
                                className="form-control"
                                value={editLng}
                                onChange={e => setEditLng(e.target.value)}
                                required
                              />
                            </div>
                            {editLatLngError && <div className="alert alert-danger py-1">{editLatLngError}</div>}
                            {editLatLngSuccess && <div className="alert alert-success py-1">{editLatLngSuccess}</div>}
                            <div className="d-flex gap-2">
                              <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                onClick={handleEditLatLngSubmit}
                              >
                                Save
                              </button>
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={handleEditLatLngCancel}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    <Form.Group controlId="location" className="mb-3">
                      <Form.Label>Select Location</Form.Label>
                      <Form.Select name="location_id" onChange={handleLocationChange} value={selectedLocation}>
                        <option>Select Location</option>
                        {locations.map((location) => (
                          <option key={location.id} value={location.id}>
                            {location.address}, {location.city}, {location.state}
                          </option>
                        ))}
                      </Form.Select>
                      <Button variant="link" onClick={handleToggleCreateLocation}>
                        {creatingNewLocation ? 'Cancel' : 'Create New Location'}
                      </Button>
                    </Form.Group>

                    {creatingNewLocation && (
                      <>
                        <Form.Group controlId="address" className="mb-3">
                          <Form.Label>Address</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Address"
                            name="address"
                            onChange={handleChange}
                            value={formData.address}
                          />
                        </Form.Group>
                        <Form.Group controlId="unit" className="mb-3">
                          <Form.Label>Unit</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Unit"
                            name="unit"
                            onChange={handleChange}
                            value={formData.unit}
                          />
                        </Form.Group>
                        <Form.Group controlId="city" className="mb-3">
                          <Form.Label>City</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="City"
                            name="city"
                            onChange={handleChange}
                            value={formData.city}
                          />
                        </Form.Group>
                        <Form.Group controlId="state" className="mb-3">
                          <Form.Label>State</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="State"
                            name="state"
                            onChange={handleChange}
                            value={formData.state}
                          />
                        </Form.Group>
                        <Form.Group controlId="zipcode" className="mb-3">
                          <Form.Label>Zip Code</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Zip Code"
                            name="zipcode"
                            onChange={handleChange}
                            value={formData.zipcode}
                          />
                        </Form.Group>
                        <Button variant="secondary" onClick={createNewLocation}>
                          Save Location
                        </Button>
                      </>
                    )}

                    <Form.Group controlId="group" className="mb-3">
                      <Form.Label>Select Group</Form.Label>
                      <Form.Select name="group_id" onChange={handleGroupChange} value={selectedGroup}>
                        <option>Select Group</option>
                        {groups.map((group) => (
                          <option key={group.id} value={group.id}>
                            {group.group_name} (Max Amps: {group.total_max_amp})
                          </option>
                        ))}
                      </Form.Select>
                      <Button variant="link" onClick={() => setCreatingNewGroup(!creatingNewGroup)}>
                        {creatingNewGroup ? 'Cancel' : 'Create New Group'}
                      </Button>
                    </Form.Group>

                    {creatingNewGroup && (
                      <>
                        <Form.Group controlId="group_name" className="mb-3">
                          <Form.Label>Group Name</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Group Name"
                            name="group_name"
                            onChange={handleChange}
                            value={formData.group_name}
                          />
                        </Form.Group>
                        <Form.Group controlId="total_max_amp" className="mb-3">
                          <Form.Label>Total Max Amps</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Total Max Amps"
                            name="total_max_amp"
                            onChange={handleChange}
                            value={formData.total_max_amp}
                          />
                        </Form.Group>
                        <Form.Group controlId="utility_id" className="mb-3">
                          <Form.Label>Utility ID</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Utility ID"
                            name="utility_id"
                            onChange={handleChange}
                            value={formData.utility_id}
                          />
                        </Form.Group>

                        <Button variant="secondary" onClick={createNewGroup}>
                          Save Group
                        </Button>
                      </>
                    )}

                    <Form.Group controlId="sim_iccid" className="mb-3">
                      <Form.Label>SIM Card ICCID</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="SIM Card ICCID"
                        name="sim_iccid"
                        onChange={handleChange}
                        value={formData.sim_iccid}
                      />
                    </Form.Group>
                    <Form.Group controlId="serial_number" className="mb-3">
                      <Form.Label>Charger Serial Number</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Charger Serial Number"
                        name="serial_number"
                        onChange={handleChange}
                        value={formData.serial_number}
                      />
                    </Form.Group>
                    <Form.Group controlId="charger_model_name" className="mb-3">
                      <Form.Label>Model Name</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Model Name"
                        name="charger_model_name"
                        onChange={handleChange}
                        value={formData.charger_model_name}
                      />
                    </Form.Group>
                    <Form.Group controlId="price_per_kwh" className="mb-3">
                      <Form.Label>Base Price Per KWh</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Price Per KWh"
                        name="price_per_kwh"
                        onChange={handleChange}
                        value={formData.price_per_kwh}
                      />
                    </Form.Group>
                    <Form.Group controlId="charger_amps" className="mb-3">
                      <Form.Label>Charger Amps</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Charger Amps"
                        name="charger_amps"
                        onChange={handleChange}
                        value={formData.charger_amps}
                      />
                    </Form.Group>
                    <Form.Group controlId="charging_rate" className="mb-3">
                      <Form.Label>Charging Rate (KWh)</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Charging Rate"
                        name="charging_rate"
                        onChange={handleChange}
                        value={formData.charging_rate}
                      />
                    </Form.Group>
                    
                    {/* Multi-port configuration */}
                    <Card className="mb-3">
                      <Card.Header>
                        <h5 className="mb-0">Port Configuration</h5>
                      </Card.Header>
                      <Card.Body>
                        <div className="d-flex justify-content-between mb-3">
                          <h6>Number of Ports: {numPorts}</h6>
                          <div>
                            <Button 
                              variant="outline-primary" 
                              size="sm" 
                              onClick={addPort}
                              className="me-2"
                            >
                              Add Port
                            </Button>
                            <Button 
                              variant="outline-danger" 
                              size="sm" 
                              onClick={() => removePort(formData.ports.length - 1)}
                              disabled={formData.ports.length <= 1}
                            >
                              Remove Port
                            </Button>
                          </div>
                        </div>
                        
                        {formData.ports.map((port, index) => (
                          <div key={index} className="border p-3 mb-3 rounded">
                            <h6>Port {index + 1}</h6>
                            <Row>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Connector Type</Form.Label>
                                  <Form.Select
                                    value={port.connector_type_id}
                                    onChange={(e) => handlePortChange(index, 'connector_type_id', e.target.value)}
                                  >
                                    {connectorTypes.map((type) => (
                                      <option key={type.id} value={type.id}>
                                        {type.name} - {type.description}
                                      </option>
                                    ))}
                                  </Form.Select>
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Maximum Power (kW)</Form.Label>
                                  <Form.Control
                                    type="number"
                                    step="0.1"
                                    placeholder="Max Power in kW"
                                    value={port.max_power_kw}
                                    onChange={(e) => handlePortChange(index, 'max_power_kw', e.target.value)}
                                  />
                                </Form.Group>
                              </Col>
                            </Row>
                            <Row>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Port Amperage</Form.Label>
                                  <Form.Control
                                    type="number"
                                    step="0.1"
                                    placeholder="Amperage for this port"
                                    value={port.port_amps}
                                    onChange={(e) => handlePortChange(index, 'port_amps', e.target.value)}
                                  />
                                  <Form.Text className="text-muted">
                                    Electrical capacity of this port in amps
                                  </Form.Text>
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Charging Rate (kWh)</Form.Label>
                                  <Form.Control
                                    type="number"
                                    step="0.1"
                                    placeholder="Charging rate in kWh"
                                    value={port.charging_rate}
                                    onChange={(e) => handlePortChange(index, 'charging_rate', e.target.value)}
                                  />
                                  <Form.Text className="text-muted">
                                    Energy delivery rate for this port
                                  </Form.Text>
                                </Form.Group>
                              </Col>
                            </Row>
                            <Form.Group className="mb-3">
                              <Form.Label>OCPP Connector ID</Form.Label>
                              <Form.Control
                                type="number"
                                placeholder="OCPP Connector ID"
                                value={port.ocpp_connector_id}
                                onChange={(e) => handlePortChange(index, 'ocpp_connector_id', e.target.value)}
                              />
                              <Form.Text className="text-muted">
                                ID used in OCPP communication for this port
                              </Form.Text>
                            </Form.Group>
                          </div>
                        ))}
                      </Card.Body>
                    </Card>
                    
                    {/* Negotiated rates section */}
                    <Card className="mb-3">
                      <Card.Header>
                        <h5 className="mb-0">Pricing Agreement</h5>
                      </Card.Header>
                      <Card.Body>
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Fixed Markup ($)</Form.Label>
                              <Form.Control
                                type="number"
                                step="0.01"
                                placeholder="Fixed Markup"
                                value={formData.negotiated_rate.fixed_markup}
                                onChange={(e) => handleRateChange('fixed_markup', e.target.value)}
                              />
                              <Form.Text className="text-muted">
                                Fixed amount added to each transaction
                              </Form.Text>
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Owner Percentage (%)</Form.Label>
                              <Form.Control
                                type="number"
                                min="0"
                                max="100"
                                placeholder="Owner Percentage"
                                value={formData.negotiated_rate.owner_percentage}
                                onChange={(e) => handleRateChange('owner_percentage', e.target.value)}
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>EV Owner Fee (%)</Form.Label>
                              <Form.Control
                                type="number"
                                min="0"
                                max="100"
                                placeholder="EV Owner Fee"
                                value={formData.negotiated_rate.ev_owner_percentage}
                                onChange={(e) => handleRateChange('ev_owner_percentage', e.target.value)}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Start Date</Form.Label>
                              <Form.Control
                                type="date"
                                value={formData.negotiated_rate.start_date}
                                onChange={(e) => handleRateChange('start_date', e.target.value)}
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                        <Form.Group className="mb-3">
                          <Form.Label>End Date (Optional)</Form.Label>
                          <Form.Control
                            type="date"
                            value={formData.negotiated_rate.end_date || ''}
                            onChange={(e) => handleRateChange('end_date', e.target.value)}
                          />
                          <Form.Text className="text-muted">
                            Leave blank for no end date
                          </Form.Text>
                        </Form.Group>
                      </Card.Body>
                    </Card>
                    
                    <Button variant="primary" type="submit" className="w-100">
                      Register Charger
                    </Button>
                  </Form>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal show={isModalOpen} onHide={closeModal}>
        <Modal.Header closeButton>
          <Modal.Title>Server Response</Modal.Title>
        </Modal.Header>
        <Modal.Body>{modalContent}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default ChargerRegistration;
